<template>
  <fragment v-if="getProductLoaded">
    <div class="formWrapper deliveryDateForm">
      <div class="formWrapper__header">
        <Icon icon="delivery" class="formWrapper__header__icon" />
        <h3>Termin dostawy</h3>
      </div>
      <div class="formWrapper__inner">
        <CustomAlert type="info" v-if="getProductLoaded">
          <span v-if="isVirtualCard && isProductNDEExternalDistribution">
            <span>Ważne! <b>Zasilenie kart rozpocznie się po pobraniu pliku zawierającego numery kart.</b></span>
            <ol class="virtual-card-instructions">
              <li><b>Poczekaj na wiadomość SMS z hasłem.</b></li>
              <li>Po otrzymaniu hasła <b>pobierz plik z numerami kart</b> i rozpocznij proces ich zasilenia.</li>
              <li><b>Przekaż użytkownikom numery kart oraz numery NIK</b> w wybrany, bezpieczny i wygodny sposób, aby mogli korzystać ze środków zgromadzonych na kartach.</li>
            </ol>
          </span>
          <span v-else-if="isVirtualCard">
            Zasilenie kart nastąpi niezwłocznie po opłaceniu zamówienia i dotarciu płatności na
            konto Pluxee, chyba że wybierzesz inaczej.
          </span>
          <span v-else-if="isProductPhysicalCard">
            Realizacja zlecenia nastąpi niezwłocznie po opłaceniu zamówienia i dotarciu płatności na
            konto Pluxee.
          </span>
          <span v-else>
            Realizacja zlecenia nastąpi w ciągu 72 godzin (dni robocze) od momentu potwierdzenia
            płatności.
          </span>
          <span v-if="getProductType === 'EC_ISS'">
            Karty zostaną doładowane w ciągu 1 dnia roboczego po otrzymaniu potwierdzenia
            dostarczenia przesyłki pod wskazany adres.
          </span>
          <span v-else-if="isProductPhysicalNDE">
            Karty zostaną zasilone i aktywowane w momencie odebrania przesyłki przez osobę
            upoważnioną lub w wybranym przez Ciebie terminie poniżej.
          </span>
        </CustomAlert>
        <div class="additionalInfo__pickers">
          <section class="additionalInfo__pickers-deliveryDate">
            <label class="required">
              {{ deliveryDateHeader }}
            </label>
            <ValidationProvider
              ref="deliveryDate"
              vid="deliveryDateValidator"
              rules="required"
              v-slot="{ errors, dirty, valid, classes }"
              name="deliveryDateValidator"
            >
              <dropdown class="form-group date-input" dropup>
                <span class="date-input__icon mi mi-calendar"></span>
                <input
                  class="form-control dropdown-toggle"
                  :class="classes"
                  name="deliveryDate"
                  type="text"
                  v-model="deliveryDate"
                  readonly="readonly"
                  placeholder="yyyy-mm-dd"
                />
                <template slot="dropdown">
                  <li>
                    <date-picker
                      v-model="deliveryDate"
                      :today-btn="false"
                      :limit-from="getDeliveryDate()"
                      :clear-btn="false"
                      :locale="localePL"
                      :week-starts-with="1"
                      :date-class="deliveryDateClass"
                      class="datepicker"
                      validationRule="required"
                      icon-control-left="mi mi-chevron-left-circle"
                      icon-control-right="mi mi-chevron-right-circle"
                    />
                  </li>
                </template>
              </dropdown>
              <ul class="errors">
                <li class="text-danger" v-for="(error, idx) in errors" :key="idx">{{ error }}</li>
              </ul>
            </ValidationProvider>
          </section>
          <section v-if="isProductPhysicalCard" class="additionalInfo__pickers-chargeDate">
            <label class="required">{{ chargeDateHeader }}</label>
            <ValidationProvider
              ref="chargeDate"
              :rules="chargeDateValidationRules"
              v-slot="{ errors, dirty, valid, classes }"
              name="chargeDateValidator"
            >
              <dropdown class="form-group date-input" dropup>
                <span class="date-input__icon mi mi-calendar"></span>
                <input
                  class="form-control dropdown-toggle"
                  :class="classes"
                  name="chargeDate"
                  type="text"
                  v-model="chargeDate"
                  readonly="readonly"
                  placeholder="yyyy-mm-dd"
                />
                <template slot="dropdown">
                  <li>
                    <date-picker
                      v-model="chargeDate"
                      :today-btn="false"
                      :limit-from="getChargeDate()"
                      :clear-btn="false"
                      :locale="localePL"
                      :week-starts-with="1"
                      :date-class="chargeDateClass"
                      class="datepicker"
                      validationRule="required"
                      icon-control-left="mi mi-chevron-left-circle"
                      icon-control-right="mi mi-chevron-right-circle"
                    />
                  </li>
                </template>
              </dropdown>
              <ul class="errors">
                <li class="text-danger" v-for="(error, idx) in errors" :key="idx">{{ error }}</li>
              </ul>
            </ValidationProvider>
          </section>
          <section v-else-if="isVirtualCard" class="additionalInfo__pickers-chargeDate">
            <label>{{ chargeTimeHeader }}</label>
            <ValidationProvider
              ref="chargeTime"
              :rules="chargeTimeValidationRules"
              v-slot="{ errors, classes }"
              name="chargeTimeValidator"
            >
              <vue-timepicker
                v-model="chargeTime"
                name="chargeTime"
                :format="chargeTimeFormat"
                :minute-interval="15"
                :hour-range="chargeTimeHourRange"
                :minute-range="chargeTimeMinuteRange"
                drop-direction="up"
                hide-clear-button
                close-on-complete
                class="time-picker"
                :input-class="[classes, 'form-control']"
              >
                <template slot="icon">
                  <span class="date-input__icon mi mi-clock"></span>
                </template>
              </vue-timepicker>
              <ul class="errors">
                <li class="text-danger" v-for="(error, idx) in errors" :key="idx">{{ error }}</li>
              </ul>
            </ValidationProvider>
          </section>
        </div>
      </div>
    </div>
    <div class="formWrapper my-5">
      <div class="formWrapper__header">
        <Icon icon="company-data" class="formWrapper__header__icon" />
        <h3>Dodatkowy komentarz do zamówienia</h3>
      </div>
      <div class="formWrapper__inner additionalInfo__comment">
        <p>
          Prosimy o umieszczenie w komentarzu wszystkich informacji istotnych dla realizacji
          zamówienia.
        </p>
        <textarea
          name="comment"
          v-model="comment"
          id="comment"
          cols="30"
          rows="10"
          placeholder="Np. inny adres dostawy;  inne działanie związane z zamówieniem"
        />
      </div>
    </div>
  </fragment>
</template>

<script>
import DatePicker from 'uiv/src/components/datepicker/DatePicker.vue';
import VueTimepicker from 'vue2-timepicker/src/vue-timepicker.vue';
import { mapFields } from 'vuex-map-fields';
import dateFormat from 'dateformat';
import { checkProductsIsVirtualCard } from '@/utils/categoryUtils';
// eslint-disable-next-line import/extensions
import localePL from '../../utils/locale/pl-PL.js';
import Icon from '../Icon.vue';
import CustomAlert from '../CustomAlert.vue';
import { isPublicHoliday, isSameDay } from '@/utils/dates';
import { getDateWithQuarterHourTimeAfterOffset } from '@/utils/delivery/dates';

const DELIVERY_EXCLUDED_DATES = ['2025-09-26', '2025-09-29'];
const CHARGE_EXCLUDED_DATES = ['2025-09-25', '2025-09-26'];
const CHARGE_TIME_MIN_OFFSET_MINUTES = 30;
const CHARGE_TIME_FORMAT = 'HH:mm';

export default {
  name: 'DeliveryAdditionalInfo',
  components: {
    DatePicker,
    Icon,
    CustomAlert,
    VueTimepicker,
  },
  data() {
    return {
      localePL,
    };
  },
  computed: {
    ...mapFields('additionalInfo', ['comment', 'deliveryDate', 'chargeDate', 'chargeTime']),
    getProductType() {
      return this.$store.getters.getProductType;
    },
    getProductLoaded() {
      return this.$store.getters.getProductLoaded;
    },
    isVirtualCard() {
      return checkProductsIsVirtualCard(this.$store.state.products.all.products[0]);
    },
    isProductCoupon() {
      return this.$store.getters.isProductCoupon;
    },
    isProductPhysicalCard() {
      return this.$store.getters.isProductPhysicalCard;
    },
    isProductPhysicalNDE() {
      return this.$store.getters.isProductPhysicalNDE;
    },
    deliveryDateHeader() {
      if (this.isVirtualCard) {
        return 'Data zasilenia kart';
      }
      if (this.isProductPhysicalNDE) {
        return 'Preferowana data dostawy';
      }
      return 'Sugerowana data dostawy:';
    },
    chargeDateHeader() {
      if (this.isProductPhysicalNDE) return 'Preferowana data zasilenia i aktywacji';
      return 'Sugerowana data doładowania';
    },
    chargeTimeHeader() {
      return 'Godzina zasilenia kart';
    },
    chargeDateValidationRules() {
      const rules = ['required'];
      if (!this.isProductPhysicalNDE) rules.push('chargeDate:deliveryDateValidator');
      return rules.join('|');
    },
    chargeTimeValidationRules() {
      const minimumAllowedDateTime = this.minimumAllowedTimeToday.getTime();
      return `chargeTime:deliveryDateValidator,${minimumAllowedDateTime}`;
    },
    isDeliveryDateToday() {
      if (!this.deliveryDate) return false;
      const selectedDate = new Date(this.deliveryDate);
      if (Number.isNaN(selectedDate.getTime())) return false;
      return isSameDay(selectedDate, new Date());
    },
    minimumAllowedTimeToday() {
      return getDateWithQuarterHourTimeAfterOffset(new Date(), CHARGE_TIME_MIN_OFFSET_MINUTES);
    },
    chargeTimeHourRange() {
      if (!this.isDeliveryDateToday) return null;

      const minimumTime = this.minimumAllowedTimeToday;
      if (!isSameDay(minimumTime, new Date())) {
        return [];
      }
      return [[minimumTime.getHours(), 23]];
    },
    chargeTimeMinuteRange() {
      if (!this.isDeliveryDateToday) return null;

      const minimumTime = this.minimumAllowedTimeToday;
      if (!isSameDay(minimumTime, new Date())) {
        return [];
      }
      const selectedHour = this.getSelectedChargeHour();
      if (selectedHour === minimumTime.getHours()) {
        return [[minimumTime.getMinutes(), 59]];
      }
      return null;
    },
    chargeTimeFormat() {
      return CHARGE_TIME_FORMAT;
    },
    isProductNDEExternalDistribution() {
      return this.$store.getters.isProductNDEExternalDistribution;
    },
  },
  methods: {
    getSelectedChargeHour() {
      if (!this.chargeTime) return null;
      const match = String(this.chargeTime).match(/^([01]\d|2[0-3]):/);
      if (!match) return null;
      return Number(match[1]);
    },
    getDateOffset() {
      if (this.isProductPhysicalCard) {
        return 1;
      }
      if (this.isVirtualCard) {
        return 0;
      }
      return 3;
    },
    getClosestWorkDayAfterOffset(dateStart, daysOffset) {
      const currentDay = dateStart.getDay();
      const dayOfWeekShift = daysOffset % 5;
      let daysShift = dayOfWeekShift;
      const newDay = currentDay + dayOfWeekShift;
      if (newDay > 5) {
        if (currentDay === 6) {
          daysShift += 1;
        } else {
          daysShift += 2;
        }
      }
      const daysToAdd = ((daysOffset - dayOfWeekShift) / 5) * 7 + daysShift;
      const result = new Date(dateStart);
      return result.setDate(result.getDate() + daysToAdd);
    },
    getDate() {
      const daysOffset = this.getDateOffset();
      const date = new Date();
      const closestWorkDay = this.getClosestWorkDayAfterOffset(date, daysOffset);
      return closestWorkDay;
    },
    getFirstValidDeliveryDate(currentDateRaw = null) {
      let firstValidDate = new Date(this.getDate());
      if (currentDateRaw) {
        const currentDate = new Date(currentDateRaw);
        firstValidDate = new Date(Math.max(firstValidDate, currentDate));
      }
      while (this.isDeliveryDateExcluded(firstValidDate)) {
        firstValidDate = new Date(this.getClosestWorkDayAfterOffset(firstValidDate, 1));
      }
      return dateFormat(firstValidDate, 'yyyy-mm-dd');
    },
    getFirstValidChargeDate(currentDateRaw) {
      const currentDate = new Date(currentDateRaw);
      const suggestedDate = new Date(this.getDate());
      let firstValidDate = new Date(Math.max(suggestedDate, currentDate));
      while (this.isChargeDateExcluded(firstValidDate)) {
        firstValidDate = new Date(this.getClosestWorkDayAfterOffset(firstValidDate, 1));
      }
      return dateFormat(firstValidDate, 'yyyy-mm-dd');
    },
    getDeliveryDate() {
      const initialDate = this.getDate();
      const finalDate = new Date(Math.max(initialDate, new Date('2023-09-26')));
      return dateFormat(finalDate, 'yyyy-mm-dd');
    },
    getChargeDate() {
      const initialDate = this.getDate();
      const finalDate = new Date(Math.max(initialDate, new Date('2023-09-23')));
      return dateFormat(finalDate, 'yyyy-mm-dd');
    },
    dateClass(date) {
      if (this.isVirtualCard) return '';
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      return isWeekend ? 'disabled' : '';
    },
    deliveryDateClass(date) {
      if (this.isDeliveryDateExcluded(date)) return 'disabled';
      return this.dateClass(date);
    },
    chargeDateClass(date) {
      if (CHARGE_EXCLUDED_DATES.includes(dateFormat(date, 'yyyy-mm-dd'))) return 'disabled';
      return this.dateClass(date);
    },
    isDeliveryDateExcluded(date) {
      if (DELIVERY_EXCLUDED_DATES.includes(dateFormat(date, 'yyyy-mm-dd'))) return true;
      if ((this.isProductPhysicalCard || this.isProductCoupon) && isPublicHoliday(date)) return true;
      return false;
    },
    isChargeDateExcluded(date) {
      return CHARGE_EXCLUDED_DATES.includes(dateFormat(date, 'yyyy-mm-dd'));
    },
  },
  created() {
    this.deliveryDate = this.getFirstValidDeliveryDate(this.deliveryDate);
    if (this.chargeDate) this.chargeDate = this.getFirstValidChargeDate(this.chargeDate);
  },
};
</script>

<style lang="scss" scoped>
.date-input .date-input__icon {
  pointer-events: none;
}

.time-picker {
  font-family: inherit;
  width: 100%;

  ::v-deep {
    input.display-time {
      padding-left: 4.8rem !important;
      width: 100%;
    }

    .dropdown.drop-up {
      bottom: 100%;
      margin-bottom: 0.6rem;
      width: fit-content;
      border: 1px solid #d1cfd7;
      box-shadow: 4px 4px #221c4633;
      height: 19.4rem;
    }

    .dropdown ul li {
      padding: 0;
      aspect-ratio: 1;
      place-content: center;
      width: 4rem;
      border: 1px solid transparent;
      transition: all 0.1s ease-in-out;
    }

    .dropdown ul li:not(.hint):not(.active):not([disabled]):hover {
      background: #f3f3fc;
      color: #1b51dc;
      border-color: #1b51dc;
    }

    .dropdown ul li:not([disabled]).active {
      border-color: #d1cfd7;

      &,
      &:active,
      &:hover,
      &:focus {
        background: #1b51dc;
      }
    }

    div.select-list {
      font-size: 1.4rem;
      font-weight: 600;
      width: fit-content;
      height: 100%;

      &::after {
        content: "";
        position: absolute;
        left: 50%;
        top: 0;
        height: 100%;
        width: 1px;
        background: #d1cfd7;
      }
    }

    .select-list .hint {
      display: none;
    }

    .minutes,
    .hours {
      scrollbar-width: thin;
      padding-inline: 2.4rem;
      margin-block: 1.6rem;
    }
  }
}
.virtual-card-instructions {
  list-style: auto;
  padding-left: 1.6rem;
  margin: 0;
}
</style>
